import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AutoComplete, Button, Input, Modal, Tooltip } from "antd";
import { Plus } from "lucide-react";

import { getUserInfo } from "../../utils/utils";
import { useAlert } from "../../context/AlertProvider";
import {
  createGroupLeadTag,
  selectedRow,
} from "../../actionCreator/leadListingActionCreater";

const TagLeads = ({
  title,
  selectedRecords,
  showTagModal,
  callback,
  closeTagModal,
}) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [tagName, setTagValues] = useState();
  const [error, setError] = useState(false);
  const [isExistingTag, setExistingTag] = useState(true);
  const [isOpen, setOpen] = useState();
  const [isAllTagged, setAllTagged] = useState(false);
  const [isCreateTag, setCreateTag] = useState(false);
  const { id } = getUserInfo();
  const tags = useSelector((state) => state.leadListingReducer?.companyTagList);

  const onTagConfrim = async () => {
    if (!!tagName) {
      setError(false);
      const payload = {
        leadIds: selectedRecords,
        tags: [tagName],
        userId: id,
        force: !!isOpen,
      };
      const res = await dispatch(createGroupLeadTag(payload));
      if (res?.response?.data?.errorKey === "alreadyTagged") {
        const numbers = res?.response?.data?.title.match(/\d+/g).map(Number);
        if (numbers[0] === numbers[1]) {
          setOpen(
            <>
              The selected leads are already Tagged in <b>{tagName}</b>
            </>,
          );
          setAllTagged(true);
        } else {
          setOpen(
            <>
              {numbers[0]} leads in your selection are already tagged under{" "}
              <b>{tagName}</b>. Would you like to tag the remaining ones?
            </>,
          );
        }
      }
      if (res?.length) {
        callback && callback(id);
        showAlert(
          "success",
          <>
            The <b>{tagName}</b> tag has been successfully added to the selected
            leads.
          </>,
        );
        dispatch(selectedRow([]));
        closeTagModal();
        setTagValues(null);
        setOpen();
        setExistingTag(true);
      }
    } else {
      setError(true);
    }
  };

  const createNewTag = async () => {
    let payload = {
      leadIds: selectedRecords,
      tags: [tagName],
      userId: id,
    };
    const res = await dispatch(createGroupLeadTag(payload));
    if (res?.length) {
      callback && callback(id);
      showAlert(
        "success",
        <>
          The new tag <b>{tagName}</b> has been successfully created and applied
          to the selected leads.
        </>,
      );
      dispatch(selectedRow([]));
      setExistingTag(true);
      setCreateTag(false);
      closeTagModal();
      setTagValues(null);
      setOpen();
    }
  };

  const handleChange = (value) => {
    setError(false);
    setTagValues(value?.trimStart());
    const existingTag = tags?.find((d) => d?.name === value);
    if (value) {
      setExistingTag(!!existingTag);
    } else {
      setExistingTag(true);
    }
  };

  return (
    <Modal
      title={null}
      width="400px"
      className="delete-popup"
      closable={true}
      centered={true}
      open={showTagModal}
      onCancel={() => {
        closeTagModal();
        setOpen();
        setTagValues(null);
        setError(false);
        setAllTagged(false);
        setExistingTag(true);
      }}
      footer={null}
    >
      <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
        {title || "Tag Lead"}
      </div>
      <div className="pop-up errorformcontainer tw-p-6">
        {isOpen ? (
          <div>
            <div>{isOpen}</div>
          </div>
        ) : (
          <>
            {isCreateTag ? (
              <div className="tw-text-xs tw-font-medium tw-text-slate-600">
                Are you sure you want to create the tag named '<b>{tagName}</b>
                '?
              </div>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                  Tag Name <span className="tw-text-red-500 tw-ml-0.5">*</span>
                </span>

                <div>
                  <AutoComplete
                    dataSource={tags?.map((d) => d?.name)}
                    value={tagName}
                    allowClear
                    className="tw-w-full tw-h-[38px]"
                    filterOption={(inputValue, option) =>
                      option.props.children
                        .toUpperCase()
                        .indexOf(inputValue.toUpperCase()) !== -1
                    }
                    onSelect={handleChange}
                    onSearch={handleChange}
                  >
                    <Input
                      className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200"
                      placeholder="Search existing or Add New Tag"
                      suffix={
                        <Tooltip title="Add New Tag">
                          <Plus
                            size={16}
                            className={`tw-text-theme tw-cursor-pointer ${
                              !isExistingTag && tagName
                                ? "tw-visible"
                                : "tw-invisible"
                            }`}
                            onClick={() => setCreateTag(true)}
                          />
                        </Tooltip>
                      }
                    />
                  </AutoComplete>
                  {!!error && (
                    <span className="tw-text-[11px] tw-text-red-500 tw-font-semibold tw-mt-1 tw-block">
                      Tag Name is required
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
        {!!isAllTagged ? (
          <Button
            key="ok"
            type="primary"
            className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold !tw-text-sm tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
            onClick={() => {
              setOpen();
              setTagValues(null);
              setError(false);
              setAllTagged(false);
              setExistingTag(true);
            }}
          >
            OK
          </Button>
        ) : (
          <>
            <Button
              key="cancel"
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-sm !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              onClick={() => {
                if (isCreateTag) {
                  setCreateTag(false);
                } else {
                  closeTagModal();
                  setOpen();
                  setTagValues(null);
                  setError(false);
                  setAllTagged(false);
                  setExistingTag(true);
                }
              }}
            >
              {isOpen ? (isAllTagged ? "" : "No") : "Cancel"}
            </Button>
            <Button
              key="ok"
              type="primary"
              className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold !tw-text-sm tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
              onClick={() => (isCreateTag ? createNewTag() : onTagConfrim())}
            >
              {isOpen
                ? isAllTagged
                  ? "OK"
                  : "Yes"
                : isCreateTag
                  ? "Yes"
                  : "Ok"}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TagLeads;
